import { toast } from 'sonner';
import { useExerciseParticipantStore } from '../../store/exercise-participant.store.js';
import i18next from 'i18next';

export class ExerciseParticipantIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchItems(page = 1, pageSize = 10, search = '', sortBy = 'name', sortOrder = 'asc', filters = {}) {
        return this._fetch({ page, pageSize, search, sortBy, sortOrder, ...filters });
    }

    async fetchAll(params = {}) {
        try {
            const response = await this.service.getAll(params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            return { success: false, data: [], error: error.message };
        }
    }

    async _fetch(params) {
        const store = useExerciseParticipantStore.getState();
        store.setLoading(true);
        store.clearError?.();

        try {
            const response = await this.service.getAll(params);
            store.setItems(response.data);

            const pagination = response.pagination || response.meta;
            if (pagination) {
                const currentPage = parseInt(pagination.page || pagination.currentPage || 1);
                const totalPages = parseInt(pagination.totalPages || 1);
                store.setPaginationData({
                    currentPage,
                    itemsPerPage: parseInt(pagination.pageSize || pagination.itemsPerPage || 10),
                    totalItems: parseInt(pagination.total || pagination.totalItems || 0),
                    totalPages,
                    hasNextPage: currentPage < totalPages,
                    hasPreviousPage: currentPage > 1
                });
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = error.message || i18next.t('integrationToasts:anErrorOccurred', { defaultValue: 'An error occurred' });
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async create(data) {
        return this._execute(async () => this.service.create(data), i18next.t('bcmExercise:messages.participant.createSuccess', { defaultValue: 'Participant created successfully' }));
    }

    async update(id, data) {
        return this._execute(async () => this.service.update(id, data), i18next.t('bcmExercise:messages.participant.updateSuccess', { defaultValue: 'Participant updated successfully' }));
    }

    async delete(id) {
        return this._execute(async () => this.service.delete(id), i18next.t('bcmExercise:messages.participant.deleteSuccess', { defaultValue: 'Participant deleted successfully' }));
    }

    async batchDelete(ids) {
        return this._executeBatch(async () => this.service.bulkDelete(ids));
    }

    async _execute(operation, successMessage) {
        const store = useExerciseParticipantStore.getState();
        store.setLoading(true);
        try {
            const response = await operation();
            toast.success(successMessage || i18next.t('integrationToasts:operationSuccessful', { defaultValue: 'Operation successful' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = error.message || i18next.t('integrationToasts:anErrorOccurred', { defaultValue: 'An error occurred' });
            toast.error(message);
            store.setError(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async _executeBatch(operation) {
        const store = useExerciseParticipantStore.getState();
        store.setLoading(true);
        try {
            const response = await operation();
            const count = response.deletedCount ?? response.data?.deletedCount;
            const msg = count !== undefined ? i18next.t('bcmExercise:messages.participant.bulkDeleteSuccess', { defaultValue: 'Selected participants deleted successfully' }) : i18next.t('bcmExercise:messages.participant.bulkDeleteSuccess', { defaultValue: 'Items deleted' });
            toast.success(msg);
            return { success: true, deletedCount: count };
        } catch (error) {
            const message = error.message || i18next.t('integrationToasts:anErrorOccurred', { defaultValue: 'An error occurred' });
            toast.error(message);
            store.setError(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }
}
